"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type AbbreviationEntry,
  type AbbreviationIssue,
  type AbbreviationReport,
  abbreviationList,
  abbreviationListCsv,
  abbreviationListHtml,
  abbreviationListText,
  checkAbbreviations,
  type SkipReason,
} from "@/lib/tool-kit/text-analysis/abbreviations";

/** English strings. A translated content file overrides these through `ui`. */
export const ABBREVIATION_CHECKER_STRINGS = {
  textLabel: "Your paper or thesis chapter",
  textPlaceholder:
    "Paste your text. Define each abbreviation at first use — “response time (RT)” — and the checker follows it from there.",
  trySample: "Try a sample",
  clear: "Clear",
  ignoreLabel: "Abbreviations to ignore",
  ignorePlaceholder: "UK, PhD, DNA",
  ignoreHint:
    "Separate with commas. Add the ones your readers know without a definition.",
  skipCommon:
    "Skip the abbreviations APA lets you leave undefined: dictionary words (AIDS, HIV, IQ, REM) and statistics (SD, OR)",
  ignoreReferences: "Ignore the reference list",
  emptyPrompt: "Paste your text to check its abbreviations.",
  noneFound:
    "No abbreviations found. The checker looks for words with two or more capital letters, such as RT, NGOs, or COVID-19.",
  results: "Results",
  statFound: "Abbreviations",
  statDefined: "Defined",
  statIssues: "Need attention",
  tableHeading: "Abbreviations in order of appearance",
  colAbbr: "Abbreviation",
  colMeaning: "Defined as",
  colUses: "Uses",
  colStatus: "Status",
  notDefined: "—",
  usesValue: "{after} after definition · {total} in all",
  statusOk: "OK",
  issues: {
    undefined: "Never defined — spell it out in full at first use.",
    "before-definition":
      "Used before it is defined — move the definition to the first use.",
    "multiple-definitions":
      "Defined more than once — define it once in the body text (a separate abstract or table note may define it again).",
    "few-uses":
      "Used fewer than three times after its definition — APA suggests writing the term out instead.",
    respelled:
      "Spelled out again after its definition — APA asks you to use the abbreviation consistently.",
  } as Record<AbbreviationIssue, string>,
  listHeading: "List of Abbreviations",
  listHint:
    "Every defined abbreviation, alphabetized — ready for the front matter of a thesis or report. Copying pastes a real table into Word or Google Docs.",
  listEmpty: "No defined abbreviations yet.",
  listAbbr: "Abbreviation",
  listMeaning: "Meaning",
  copyList: "Copy table",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFilename: "list-of-abbreviations.csv",
  skippedLine: "Skipped: {list}",
  skippedItem: "{abbr} ({reason})",
  skipReasons: {
    dictionary: "dictionary word",
    statistic: "statistic",
    ignored: "on your ignore list",
    roman: "Roman numeral",
  } as Record<SkipReason, string>,
  highlightHeading: "Your text, abbreviations highlighted",
  highlightLabel: "Your text with abbreviations highlighted",
  legendDefinition: "Definition",
  legendOk: "Use",
  legendIssue: "Needs attention",
  sample:
    "Abstract\nThis study tested whether working memory (WM) predicts reading speed in adults learning a second language. WM was measured with an operation span task.\n\nMethod\nWe recruited 60 adults through local non-governmental organizations (NGOs). Response time (RT) was recorded in milliseconds. RT outliers were trimmed, mean RT was computed for each participant, and RT was compared across conditions. The NGOs also provided meeting rooms. Participants completed an EEG session, and an ANOVA tested group differences. Working memory scores were standardized, and WM correlated with RT. An analysis of variance (ANOVA) followed.",
} as const;

type Strings = typeof ABBREVIATION_CHECKER_STRINGS;

const TONES = {
  definition: "sky",
  ok: "emerald",
  issue: "amber",
} as const;

const LIST_SEPARATOR_RE = /[,;\n]/;

function parseIgnore(raw: string): Set<string> {
  return new Set(
    raw
      .split(LIST_SEPARATOR_RE)
      .map((item) => item.trim().toUpperCase())
      .filter(Boolean)
  );
}

function highlightMatches(report: AbbreviationReport): TextMatch[] {
  const matches: TextMatch[] = [];
  for (const entry of report.entries) {
    const meaning = entry.definitions[0]?.longForm;
    const title = meaning ? `${entry.abbr} — ${meaning}` : entry.abbr;
    for (const definition of entry.definitions) {
      matches.push({
        start: definition.start,
        end: definition.end,
        category: "definition",
        title,
      });
    }
    const category = entry.issues.length > 0 ? "issue" : "ok";
    for (const use of entry.uses) {
      matches.push({ start: use.start, end: use.end, category, title });
    }
  }
  return matches;
}

function EntryRow({
  entry,
  strings,
}: {
  entry: AbbreviationEntry;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <tr>
      <td className={`${TD} font-medium`}>{entry.abbr}</td>
      <td className={TD}>
        {entry.definitions[0]?.longForm ?? strings.notDefined}
      </td>
      <td className={`${TD} whitespace-nowrap`}>
        {entry.definitions.length > 0
          ? t(strings.usesValue, {
              after: fmt(entry.usesAfterDefinition),
              total: fmt(entry.uses.length),
            })
          : fmt(entry.uses.length)}
      </td>
      <td className={TD}>
        {entry.issues.length === 0 ? (
          strings.statusOk
        ) : (
          <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
            {entry.issues.map((issue) => (
              <li key={issue}>{strings.issues[issue]}</li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

function AbbreviationTable({
  report,
  strings,
}: {
  report: AbbreviationReport;
  strings: Strings;
}) {
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colAbbr}
              </th>
              <th className={TH} scope="col">
                {strings.colMeaning}
              </th>
              <th className={TH} scope="col">
                {strings.colUses}
              </th>
              <th className={TH} scope="col">
                {strings.colStatus}
              </th>
            </tr>
          </thead>
          <tbody>
            {report.entries.map((entry) => (
              <EntryRow entry={entry} key={entry.abbr} strings={strings} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ListOfAbbreviations({
  report,
  strings,
}: {
  report: AbbreviationReport;
  strings: Strings;
}) {
  const rows = abbreviationList(report);
  const head = [strings.listAbbr, strings.listMeaning] as const;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
      <p className={HINT}>{strings.listHint}</p>
      {rows.length === 0 ? (
        <Notice>{strings.listEmpty}</Notice>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH} scope="col">
                    {strings.listAbbr}
                  </th>
                  <th className={TH} scope="col">
                    {strings.listMeaning}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.abbr}>
                    <td className={`${TD} font-medium`}>{row.abbr}</td>
                    <td className={TD}>{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              html={abbreviationListHtml(rows, head)}
              label={strings.copyList}
              text={abbreviationListText(rows)}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                downloadText(
                  strings.csvFilename,
                  abbreviationListCsv(rows, head),
                  "text/csv;charset=utf-8"
                )
              }
              type="button"
            >
              {strings.downloadCsv}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Results({
  text,
  report,
  strings,
}: {
  text: string;
  report: AbbreviationReport;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const defined = report.entries.filter((e) => e.definitions.length > 0).length;
  const flagged = report.entries.filter((e) => e.issues.length > 0).length;
  const legend = [
    [TONES.definition, strings.legendDefinition],
    [TONES.ok, strings.legendOk],
    [TONES.issue, strings.legendIssue],
  ] as const;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          label={strings.statFound}
          value={fmt(report.entries.length)}
        />
        <StatCard label={strings.statDefined} value={fmt(defined)} />
        <StatCard emphasis label={strings.statIssues} value={fmt(flagged)} />
      </StatGrid>
      {report.entries.length === 0 ? (
        <Notice>{strings.noneFound}</Notice>
      ) : (
        <AbbreviationTable report={report} strings={strings} />
      )}
      {report.skipped.length > 0 ? (
        <p className={HINT}>
          {t(strings.skippedLine, {
            list: report.skipped
              .map((skip) =>
                t(strings.skippedItem, {
                  abbr: skip.abbr,
                  reason: strings.skipReasons[skip.reason],
                })
              )
              .join(", "),
          })}
        </p>
      ) : null}
      <ListOfAbbreviations report={report} strings={strings} />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightHeading}</h3>
        <ul className="flex flex-wrap gap-3 text-muted-foreground text-xs">
          {legend.map(([tone, label]) => (
            <li key={label}>
              <span className={HIGHLIGHT_TONES[tone]}>{label}</span>
            </li>
          ))}
        </ul>
        <HighlightedText
          label={strings.highlightLabel}
          matches={highlightMatches(report)}
          text={text}
          tones={TONES}
        />
      </div>
    </section>
  );
}

export default function AbbreviationCheckerWidget() {
  const strings = useToolUi(ABBREVIATION_CHECKER_STRINGS);
  const [text, setText] = useState("");
  const [ignoreRaw, setIgnoreRaw] = useState("");
  const [skipCommon, setSkipCommon] = useState(true);
  const [ignoreReferences, setIgnoreReferences] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () =>
      deferred.trim()
        ? checkAbbreviations(deferred, {
            ignore: parseIgnore(ignoreRaw),
            skipCommon,
            ignoreReferences,
          })
        : null,
    [deferred, ignoreRaw, skipCommon, ignoreReferences]
  );

  return (
    <>
      <TextAreaField
        id="abc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={12}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <TextField
        hint={strings.ignoreHint}
        id="abc-ignore"
        label={strings.ignoreLabel}
        onChange={setIgnoreRaw}
        placeholder={strings.ignorePlaceholder}
        value={ignoreRaw}
      />
      <div className="space-y-2">
        <Checkbox
          checked={skipCommon}
          label={strings.skipCommon}
          onChange={setSkipCommon}
        />
        <Checkbox
          checked={ignoreReferences}
          label={strings.ignoreReferences}
          onChange={setIgnoreReferences}
        />
      </div>
      {report ? (
        <Results report={report} strings={strings} text={deferred} />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
